import React from 'react'
import { Alert, Container, Row, Col, Form } from 'reactstrap'
import ICSVehiclePicker from '../../../components/ICSVehiclePicker/ICSVehiclePicker'
import { useForm, useWatch } from 'react-hook-form'
import ICSDateRange from '../../../components/ICSDateRange/ICSDateRange'
import { FileExtensionPicker } from './FileExtensionPicker'
import ComponentBox from '../../../components/ComponentBox/ComponentBox'
import { useDispatch } from 'react-redux'
import { DataSearchRequest } from '../../../services/endpoints/data'
import { searchDataThunk } from '../../../redux/slices'
import { Collection } from './CollectionPicker'
import ICSButton from '../../../components/ICS/Button/ICSButton'
import { DataSpyEnabled } from './DataSpyEnabled'
import { SearchSettings } from './SearchSettings'
import { IdChipInput } from './IdChipInput'
import { useAppSelector } from '../../../hooks/useAppSelector'
import "./SearchBox.css"
import { useTimezone } from '../../../hooks/useTimezone'

export interface _DataSearchValues {
    vehiclePicker: {
        fleets: string[],
        vehicles: string[]
    },
    dateRange: {
        start: Date | null,
        end: Date | null
    },
    collections: string[],
    fileTypes: string[],
    uploadIds: number[],
    fileVersionIds: number[],
    max: number
}

const SearchBox: React.FC = () => {
    const {formatUtcTimeFromLocal} = useTimezone()
    const { handleSubmit, control, formState: { isSubmitting } } = useForm<_DataSearchValues>({
        defaultValues: {
            vehiclePicker: {
                fleets:[],
                vehicles: []
            },
            dateRange: {
                start: null,
                end: null
            },
            collections: [],
            fileTypes: [],
            uploadIds: [],
            fileVersionIds: [],
            max: 50
        }
    })
    const dispatch = useDispatch()
    const unmatchedFileVersionIds = useAppSelector((state) => state.data.unmatchedFileVersionIds)

    const watchedVehiclePicker = useWatch({ control, name: 'vehiclePicker' })
    const watchedUploadIds = useWatch({ control, name: 'uploadIds' })
    const watchedFileVersionIds = useWatch({ control, name: 'fileVersionIds' })
    const hasVehicleFilters = (watchedVehiclePicker?.fleets?.length > 0) || (watchedVehiclePicker?.vehicles?.length > 0)
    const hasIdFilters = (watchedUploadIds?.length > 0) || (watchedFileVersionIds?.length > 0)
    const canSearch = hasVehicleFilters || hasIdFilters
    
    const searchData = (data: _DataSearchValues) => {
        const hasSubmittedVehicleFilters = (data.vehiclePicker.fleets.length > 0) || (data.vehiclePicker.vehicles.length > 0)
        const hasSubmittedIdFilters = (data.uploadIds.length > 0) || (data.fileVersionIds.length > 0)

        if (!hasSubmittedVehicleFilters && !hasSubmittedIdFilters) {
            return
        }

        const req:DataSearchRequest = {
            fleetId: data.vehiclePicker.fleets,
            vehicleId: data.vehiclePicker.vehicles,
            extensions: data.fileTypes,
            names: data.collections,
            startDate: formatUtcTimeFromLocal(data.dateRange.start),
            endDate: formatUtcTimeFromLocal(data.dateRange.end),
            sort: 'uploadId',
            order: 'desc',
            offset: 0,
            max: data.max,
            onlyGroups: true,
            ...(data.uploadIds.length > 0 && { uploadId: data.uploadIds }),
            ...(data.fileVersionIds.length > 0 && { fileVersions: data.fileVersionIds }),
        }

        dispatch(searchDataThunk(req))
    }

    /** some testing and a reminder when RHF gets fixed for the ICSVehiclePickerProps issue (and others)
    const t = <T, K extends Path<T>>(v: PathValue<T, K>) => v
    const x = t<_DataSearchValues, "vehiclePicker">({vehicles: [], fleets: []})
    type Test = PathValue<_DataSearchValues, "vehiclePicker">
    //*/


    return (
        <Container fluid={true}>
             <Form onSubmit={handleSubmit(searchData)}>
                    <ComponentBox isCustom={true} cardClass='groups-results-container' cardBodyId='groupsCardBody' title="Search Criteria" accordion={true}>
                        <Row>
                            <Col xs={12} md={6} >
                                <ICSVehiclePicker<_DataSearchValues, "vehiclePicker">
                                    name="vehiclePicker"
                                    control={control}
                                    required={false}
                                />
                            </Col>
                            <Col xs={12} md={6} >
                                <div>
                                    <div className="spacer">
                                        <h4>Date Range</h4>
                                        <ICSDateRange name="dateRange" control={control} />
                                    </div>
                                    <div className="spacer">
                                        <h4>Collection or File Name</h4>
                                        <Collection name="collections" control={control} />
                                    </div>
                                    <div className="spacer">
                                        <div style={{ display: 'flex', gap: '1rem' }}>
                                            <div style={{ flex: 1 }}>
                                                <IdChipInput<_DataSearchValues>
                                                    name="uploadIds"
                                                    control={control}
                                                    label="Upload Id(s)"
                                                    placeholder="Type upload id and press enter"
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <IdChipInput<_DataSearchValues>
                                                    name="fileVersionIds"
                                                    control={control}
                                                    label="File Id(s)"
                                                    placeholder="Type file id and press enter"
                                                />
                                                {unmatchedFileVersionIds.length > 0 && (
                                                    <Alert color="warning" style={{ marginTop: 6, marginBottom: 0, padding: '6px 10px', marginLeft: 0, marginRight: 0 }}>
                                                        Unmatched File Id(s): {unmatchedFileVersionIds.join(', ')}
                                                    </Alert>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="spacer">
                                        <h4>File Extensions</h4>
                                        <FileExtensionPicker name="fileTypes" control={control} />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row className='dataSearchButtonsRow'>
                            <Col className='dataSearchButtonCol'>
                                <DataSpyEnabled />
                                <SearchSettings />
                            </Col>
                            <Col className='col-sm-auto dataSearchButtonCol'>
                                <ICSButton
                                    className='dataSearchButton'
                                    icon="search"
                                    text="Search"
                                    type="success"
                                    onClick={handleSubmit(searchData)}
                                    disabled={ isSubmitting || !canSearch } 
                                />
                            </Col>
                        </Row>
                </ComponentBox>
            </Form>
        </Container>
    )
}

export default SearchBox
